// src/utils/analytics.ts
//
// Consent-gated analytics installer. Nothing loads from
// googletagmanager / google-analytics until `enableAnalytics()`
// is called by the cookie banner accept path.
//
// Why not the inline gtag snippet in index.html:
//   - The inline snippet fires on page load, before the user has
//     a chance to consent. In EU jurisdictions this is a GDPR
//     issue (analytics is not "strictly necessary").
//   - Once the user declines, the inline snippet has already
//     loaded the library. We can't unload it.
//
// What this module does:
//   - On `enableAnalytics()`: dynamically inserts the gtag
//     <script> + boots the dataLayer + sends initial config.
//     Idempotent — calling twice is a no-op.
//   - On `disableAnalytics()`: sets the GA opt-out flag the
//     library checks (`window['ga-disable-<measurementId>']`)
//     and removes the script tag if present. Future page
//     loads will not re-install the script (the cookie banner
//     gates that).
//   - `trackEvent(name, params)`: enqueues into the dataLayer
//     buffer. Safe to call before `enableAnalytics`; events
//     queue and flush when the script loads.
//
// The Web Vitals reporter (main.tsx) routes through
// `trackEvent` so it respects the same consent gate.

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

let installed = false;
let installing = false;
const SCRIPT_ID = 'ga4-installed-script';

function bootDataLayer() {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    // Standard GA bootstrap: gtag pushes to dataLayer until
    // gtag/js loads and replaces this with the real function.
    window.gtag = function gtag(...args: any[]) {
      window.dataLayer!.push(args);
    };
  }
}

/** Call after the user accepts the analytics consent category.
 *  Safe to call multiple times. */
export function enableAnalytics(): void {
  if (typeof window === 'undefined') return;
  if (!GA_ID) {
    // No measurement id configured — nothing to install. We still
    // boot the dataLayer so trackEvent calls don't throw.
    bootDataLayer();
    return;
  }
  if (installed || installing) return;
  installing = true;

  bootDataLayer();
  // Clear any prior opt-out flag in case the user just toggled
  // analytics back on.
  delete window[`ga-disable-${GA_ID}`];

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    GA_ID
  )}`;
  script.onload = () => {
    installed = true;
    installing = false;
    window.gtag?.('js', new Date());
    window.gtag?.('config', GA_ID, {
      // Anonymize IP at GA's end. Doesn't replace data-protection
      // commitments but reduces the surface.
      anonymize_ip: true,
      // Don't auto-send page_view; we'll send explicitly so SPA
      // route changes are tracked correctly. (Wire on react-router
      // location changes if/when needed.)
      send_page_view: false,
    });
  };
  script.onerror = () => {
    installed = false;
    installing = false;
  };
  document.head.appendChild(script);
}

/** Disable analytics for the current session. Call from the
 *  cookie banner's decline / preference toggle. */
export function disableAnalytics(): void {
  if (typeof window === 'undefined') return;
  if (GA_ID) {
    window[`ga-disable-${GA_ID}`] = true;
  }
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    existing.remove();
  }
  installed = false;
}

/** Check the persisted cookie-consent state and install if the
 *  user previously accepted analytics. Call once at app boot. */
export function applyStoredAnalyticsConsent(): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem('instant-tours-cookie-consent');
    if (!raw) return;
    const parsed = JSON.parse(raw) as { analytics?: boolean };
    if (parsed.analytics === true) {
      enableAnalytics();
    } else {
      disableAnalytics();
    }
  } catch {
    // Bad JSON / quota / private mode — treat as not-consented.
  }
}

/** Send a custom event. No-ops cleanly if analytics is disabled
 *  or the GA script never loaded. */
export function trackEvent(
  name: string,
  params?: Record<string, string | number | boolean>
): void {
  if (typeof window === 'undefined') return;
  bootDataLayer();
  // gtag will buffer until the script loads, then flush.
  window.gtag?.('event', name, params || {});
}
